import { useCallback } from "react"
import { Trans, useTranslation } from "react-i18next"

import { TOptions } from "i18next"
import { LangArguments, langKeys, LangType } from "src/domains/i18n/lang-keys"

/**
 * Use this count value when you want to force the plural ("_other") form of a
 * translation key. i18next uses count to determine singular vs plural forms:
 * - count === 1 → uses base key (singular)
 * - count !== 1 → uses "_other" key (plural)
 *
 * @example
 * // Instead of: t(langKeys.home_other)
 * // Use:        t(langKeys.home, { count: FORCE_PLURAL_COUNT })
 *
 * @knipIgnore
 */
export const FORCE_PLURAL_COUNT = 100

export type TLangKeys = typeof langKeys
export type TLangKey = keyof TLangKeys

export type TFunctionArgs<T extends TLangKey> = T extends keyof LangArguments
  ? [T, TOptions<LangArguments[T]>]
  : [T, TOptions<object>?]

export function useTranslate() {
  const { t: _t, i18n } = useTranslation()

  /** Convenience function for adding placeholder translations.
   *
   * Prefer this over `t` + langKeys.placeholder_web when you need to add a
   * placeholder translation; the reference lookup + deprecation indicator will
   * make usage of this function easier to find/spot.
   *
   * @deprecated
   */
  function tPlaceholder(text: string, _key?: string) {
    return t(langKeys.placeholder_web, { text })
  }

  /** Apply translation to a key. */
  const t = useCallback(
    <T extends TLangKey>(...args: TFunctionArgs<T>): LangType[T] => {
      const [key, options] = args
      if (options !== undefined) {
        return _t(
          key as string,
          options as Record<string, unknown>
        ) as LangType[T]
      }
      return _t(key as string) as LangType[T]
    },
    [_t]
  )

  return {
    Trans,
    t,
    langKeys,
    i18n,
    tPlaceholder,
    /** A reference to the react-i18next `t` function.
     *
     * Use this only when you *know* you need to access the raw `i18n.t`
     * function and the stronger typed `t` function is insufficient.

     * Use `t` instead, whenever possible; this function does not perform any
     * lang key type checking.
    */
    _t,
  }
}

export type TTPlaceholder = ReturnType<typeof useTranslate>["tPlaceholder"]
